import * as React from 'react';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { TimePicker12H } from './time-picker-12h';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from './ui/drawer';
import {
  CalendarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Clock,
} from 'lucide-react';
import { useIsMobile } from './hooks/use-mobile';

// Business weeks run Monday through Sunday, unlike dayjs' locale default.
dayjs.extend(isoWeek);

const shortcutItems = [
  {
    label: 'Today',
    getValue: () => {
      const today = dayjs();
      return { startDate: today, endDate: today };
    },
  },
  {
    label: 'Yesterday',
    getValue: () => {
      const yesterday = dayjs().subtract(1, 'day');
      return { startDate: yesterday, endDate: yesterday };
    },
  },
  {
    label: 'This Week',
    getValue: () => {
      const today = dayjs();
      return {
        startDate: today.startOf('isoWeek'),
        endDate: today.endOf('isoWeek'),
      };
    },
  },
  {
    label: 'Last Week',
    getValue: () => {
      const today = dayjs();
      const prevWeek = today.subtract(1, 'week');
      return {
        startDate: prevWeek.startOf('isoWeek'),
        endDate: prevWeek.endOf('isoWeek'),
      };
    },
  },
  {
    label: 'Last 7 Days',
    getValue: () => {
      const today = dayjs();
      return {
        startDate: today.subtract(6, 'day'),
        endDate: today,
      };
    },
  },
  {
    label: 'This Month',
    getValue: () => {
      const today = dayjs();
      return {
        startDate: today.startOf('month'),
        endDate: today.endOf('month'),
      };
    },
  },
  {
    label: 'Last Month',
    getValue: () => {
      const today = dayjs();
      const lastMonth = today.subtract(1, 'month');
      return {
        startDate: lastMonth.startOf('month'),
        endDate: lastMonth.endOf('month'),
      };
    },
  },
  {
    label: 'Last 30 Days',
    getValue: () => {
      const today = dayjs();
      return {
        startDate: today.subtract(29, 'day'),
        endDate: today,
      };
    },
  },
];

export default function DateRangePicker({
  onDateRangeChange,
  onTimeRangeChange,
  onLocalTimeChange,
  onApply,
  initialStartDate,
  initialEndDate,
  initialStartTime = '00:00',
  initialEndTime = '23:59',
  initialUseLocalTime = false,
  align = 'start',
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);
  const isMobile = useIsMobile();

  // Stable initial values to prevent flickering
  const initialStart = React.useMemo(
    () =>
      initialStartDate ? dayjs(initialStartDate) : dayjs().subtract(7, 'day'),
    [initialStartDate]
  );

  const initialEnd = React.useMemo(
    () => (initialEndDate ? dayjs(initialEndDate) : dayjs()),
    [initialEndDate]
  );

  const [startDate, setStartDate] = React.useState(initialStart);
  const [endDate, setEndDate] = React.useState(initialEnd);
  const [startTime, setStartTime] = React.useState(initialStartTime);
  const [endTime, setEndTime] = React.useState(initialEndTime);
  const [_useLocalTime, setUseLocalTime] = React.useState(initialUseLocalTime);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  // Parent callbacks are inline arrows, so they change identity on every render;
  // holding them in a ref keeps the commit effect tied to the selection alone.
  const callbacksRef = React.useRef({});
  callbacksRef.current = {
    onDateRangeChange,
    onTimeRangeChange,
    onLocalTimeChange,
  };

  const committedRef = React.useRef(null);

  // The parent mirrors the visible selection as soon as it changes, so
  // generating a report can never fall back to the previously applied range
  // when the picker is dismissed without pressing Apply Range.
  const commitSelection = React.useCallback(() => {
    if (!startDate || !endDate) return;

    const startStr = startDate.format('YYYY-MM-DD');
    const endStr = endDate.format('YYYY-MM-DD');
    const signature = `${startStr}|${endStr}|${startTime}|${endTime}`;
    if (committedRef.current === signature) return;
    committedRef.current = signature;

    const callbacks = callbacksRef.current;
    callbacks.onDateRangeChange?.(startStr, endStr);
    callbacks.onTimeRangeChange?.(startTime, endTime);
    // Ranges are always interpreted in the business timezone.
    callbacks.onLocalTimeChange?.(false);
  }, [startDate, endDate, startTime, endTime]);

  React.useEffect(() => {
    commitSelection();
  }, [commitSelection]);

  const formatDate = (date) => {
    if (!date) return '';
    return date.format('MMM DD, YYYY');
  };

  // Convert 24-hour format (HH:MM) to 12-hour format (h:MM AM/PM)
  const formatTime12H = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':').map(Number);
    const hour12 = hours % 12 || 12;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    return `${hour12}:${String(minutes || 0).padStart(2, '0')} ${ampm}`;
  };

  // Derived rather than stored, so the highlight survives reopening the picker
  // and remounts (the parent only keeps the dates, not which preset was used).
  const activeShortcut = React.useMemo(() => {
    if (!startDate || !endDate) return null;
    if (startTime !== '00:00' || endTime !== '23:59') return null;

    const match = shortcutItems.find((shortcut) => {
      const range = shortcut.getValue();
      return (
        range.startDate.isSame(startDate, 'day') &&
        range.endDate.isSame(endDate, 'day')
      );
    });
    return match ? match.label : null;
  }, [startDate, endDate, startTime, endTime]);

  const formatDisplayDate = React.useMemo(() => {
    if (startDate && endDate) {
      const startDateStr = formatDate(startDate);
      const endDateStr = formatDate(endDate);
      const startTime12H = formatTime12H(startTime);
      const endTime12H = formatTime12H(endTime);

      if (startDate.isSame(endDate, 'day')) {
        return `${startDateStr} ${startTime12H} - ${endTime12H}`;
      } else {
        return `${startDateStr} ${startTime12H} - ${endDateStr} ${endTime12H}`;
      }
    }
    return 'Select date range';
  }, [startDate, endDate, startTime, endTime]);

  const handleStartDateChange = React.useCallback(
    (newValue) => {
      if (!newValue) return;

      // Use setTimeout to batch state updates and prevent flickering
      setTimeout(() => {
        setStartDate(newValue);

        // If end date is before new start date, update end date
        if (endDate && newValue.isAfter(endDate)) {
          setEndDate(newValue);
        }
      }, 0);
    },
    [endDate]
  );

  const handleEndDateChange = React.useCallback(
    (newValue) => {
      if (!newValue) return;

      // Use setTimeout to batch state updates and prevent flickering
      setTimeout(() => {
        setEndDate(newValue);

        // If start date is after new end date, update start date
        if (startDate && newValue.isBefore(startDate)) {
          setStartDate(newValue);
        }
      }, 0);
    },
    [startDate]
  );

  const handleShortcutClick = React.useCallback((shortcut) => {
    const newRange = shortcut.getValue();

    // Batch state updates to prevent flickering
    setTimeout(() => {
      setStartDate(newRange.startDate);
      setEndDate(newRange.endDate);

      // Set default times for preset selections
      setStartTime('00:00');
      setEndTime('23:59');

      // Don't auto-submit for shortcuts anymore - just set the values
      // User must click "Apply Custom Range" to submit
    }, 0);
  }, []);

  const handleStartTimeChange = React.useCallback((newTime) => {
    setStartTime(newTime);
  }, []);

  const handleEndTimeChange = React.useCallback((newTime) => {
    setEndTime(newTime);
  }, []);

  const _handleLocalTimeToggle = React.useCallback((checked) => {
    setUseLocalTime(checked);
  }, []);

  const handleApplyRange = React.useCallback(() => {
    // Values are already mirrored to the parent; Apply Range is the explicit
    // "use this range now" action that also runs the report.
    commitSelection();
    onApply?.();
    setIsOpen(false);
  }, [commitSelection, onApply]);

  const TriggerButton = React.forwardRef((props, ref) => (
    <Button
      ref={ref}
      type="button"
      size="lg"
      variant="outline"
      data-testid="button-date-picker"
      className="justify-start text-left font-normal w-full"
      {...props}
    >
      <div className="flex items-center gap-2 w-full">
        <CalendarIcon className="h-4 w-4" />
        <div className="flex-1 text-left">
          <div>{formatDisplayDate}</div>
        </div>
        {activeShortcut ? (
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
            {activeShortcut}
          </span>
        ) : null}
        <div className="opacity-60">
          {isOpen ? (
            <ChevronUpIcon className="h-4 w-4" />
          ) : (
            <ChevronDownIcon className="h-4 w-4" />
          )}
        </div>
      </div>
    </Button>
  ));

  TriggerButton.displayName = 'TriggerButton';

  const DateRangeContent = React.memo(() => (
    <div
      className={`p-0 ${
        isMobile ? 'min-w-full' : 'min-w-[600px] max-w-[700px]'
      }`}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <div className={`${isMobile ? 'block' : 'flex'}`}>
          {/* Left Side - Presets */}
          <div
            className={`${
              isMobile ? 'w-full border-b' : 'w-48 border-r'
            } bg-muted/30 p-4`}
          >
            <h4 className="text-sm font-medium mb-3 text-muted-foreground">
              Quick Select
            </h4>
            <div
              className={`${isMobile ? 'grid grid-cols-2 gap-2' : 'space-y-1'}`}
            >
              {shortcutItems.map((shortcut, index) => {
                const isActive = shortcut.label === activeShortcut;
                return (
                  <Button
                    key={index}
                    variant={isActive ? 'default' : 'ghost'}
                    size="sm"
                    aria-pressed={isActive}
                    onClick={() => handleShortcutClick(shortcut)}
                    className={`${
                      isMobile
                        ? 'text-xs'
                        : `w-full justify-start text-xs h-8 px-3 ${
                            isActive ? 'font-semibold' : 'font-normal'
                          }`
                    }`}
                  >
                    {shortcut.label}
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Right Side - Date Pickers and Settings */}
          <div className="flex-1 p-4 flex flex-col justify-between">
            <div className="space-y-4">
              {/* Date Pickers */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Start Date
                    </label>
                    <DatePicker
                      value={startDate}
                      onChange={handleStartDateChange}
                      maxDate={endDate || undefined}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                        },
                      }}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      End Date
                    </label>
                    <DatePicker
                      value={endDate}
                      onChange={handleEndDateChange}
                      minDate={startDate || undefined}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                        },
                      }}
                    />
                  </div>
                </div>

                {/* Time Pickers */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Start Time
                    </label>
                    <TimePicker12H
                      value={startTime}
                      onChange={handleStartTimeChange}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      End Time
                    </label>
                    <TimePicker12H
                      value={endTime}
                      onChange={handleEndTimeChange}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Timezone Note */}
              <div className="flex items-center space-x-3 pt-2 border-t">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-muted-foreground">
                    All dates and times are based on the business timezone
                  </span>
                </div>
              </div>
            </div>
            {/* Apply Range Submit */}
            <div className="flex justify-end pt-2 border-t">
              <Button
                onClick={handleApplyRange}
                size="sm"
                className={isMobile ? 'w-full' : 'px-6'}
              >
                Apply Range
              </Button>
            </div>
          </div>
        </div>
      </LocalizationProvider>
    </div>
  ));

  DateRangeContent.displayName = 'DateRangeContent';

  // Use popover as fallback until mounted to avoid hydration issues
  if (!mounted || !isMobile) {
    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <TriggerButton />
        </PopoverTrigger>
        <PopoverContent
          align={align}
          className="w-auto p-0"
          side="bottom"
          sideOffset={8}
          avoidCollisions={true}
          collisionPadding={16}
          sticky="always"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <DateRangeContent />
        </PopoverContent>
      </Popover>
    );
  }

  // Mobile drawer (only after mounted and confirmed mobile)
  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerTrigger asChild>
        <TriggerButton />
      </DrawerTrigger>
      <DrawerContent className="max-h-[90vh]">
        <DrawerHeader className="text-left">
          <DrawerTitle>Select Date Range</DrawerTitle>
        </DrawerHeader>
        <div className="overflow-auto">
          <DateRangeContent />
        </div>
      </DrawerContent>
    </Drawer>
  );
}
